import React, { useEffect, useState } from "react";
import { CaretDownFilled, DoubleLeftOutlined } from "@ant-design/icons";
import withDashboardLayout from "../../../hoc/withDashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  GetAppointment,
  GetAppointmentLogs,
  GetAppointmentReviews,
  UpdateAppointmentToCancelled,
  UpdateAppointmentToFinished,
} from "../../../utils/api";
import {
  Button,
  Divider,
  Dropdown,
  Empty,
  Modal,
  Skeleton,
  message,
} from "antd";
import { imgPath } from "../../../utils/axiosInstance";
import dayjs from "dayjs";
import Loader from "../../shared/Loader";
import StarIcon from "../../../assets/star-icon.png";
import LocationIcon from "../../../assets/location-icon.png";
import MailIcon from "../../../assets/mail-icon.png";
import PhoneIcon from "../../../assets/phone-icon.png";
import Logo from "../../../assets/logo.png";
import DateIcon from "../../../assets/date-icon.png";
import {
  commonDateFormat,
  convertToViewableAmount,
} from "../../../utils/helper";

const dropdownItems = [
  {
    label: "Completed",
    key: "finished",
    disabled: false,
  },
  {
    label: "In Progress",
    key: "upcoming",
    disabled: false,
  },
  {
    label: "Cancelled",
    key: "cancelled",
    disabled: false,
  },
];

const AppointmentsDetails = () => {
  const [appointmentData, setAppointmentData] = useState({});
  console.log(appointmentData);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [modalData, setModalData] = useState({
    visible: false,
    name: null,
    id: null,
    action: null,
    selectedStatus: null,
  });
  const navigate = useNavigate();
  const { id } = useParams();

  let updatedItems = dropdownItems.filter((item) => {
    return item.key !== appointmentData.status;
  });

  const handleActionModalOpen = (action, status, data) => {
    if (action === "delete") {
      setModalData({
        visible: true,
        name: data.name,
        id: data.id,
        action: "delete",
        selectedStatus: null,
      });
    } else if (action === "changeStatus") {
      setModalData({
        visible: true,
        name: data.name,
        id: data?.id,
        selectedStatus: status,
        action: action,
      });
    }
  };

  const handleChangeStatusOk = async () => {
    if (modalData.action === "changeStatus") {
      try {
        setBtnLoading(true);
        let response;
        if (modalData?.selectedStatus === "cancelled") {
          response = await UpdateAppointmentToCancelled(modalData?.id, {
            status: modalData?.selectedStatus,
          });
        } else {
          response = await UpdateAppointmentToFinished(modalData?.id, {
            status: modalData?.selectedStatus,
          });
        }
        setBtnLoading(false);
        if (response?.isSuccess) {
          message.success("Appointment status updated successfully");
          setModalData({
            selectedStatus: null,
            user: null,
            id: null,
            visible: false,
            action: null,
          });
          getAppointment();
        }
      } catch (error) {
        setBtnLoading(false);
        message.error("Error in updating status, Try again later");
      }
    }
  };

  const handleModalCancel = () => {
    setModalData({
      visible: false,
      selectedStatus: null,
      user: null,
      id: null,
      action: null,
    });
  };

  const getAppointment = async () => {
    try {
      setLoading(true);
      const response = await GetAppointment(id);
      if (response?.isSuccess) {
        setAppointmentData(response?.data);
        setLoading(false);
      } else {
        message.error("Failed to get data, please try again later.");
        setLoading(false);
        navigate("/admin/appoitments");
      }
    } catch (error) {
      setLoading(false);
      message.error(error?.message);
    }
  };

  const nonRefundableAmount = (data) => {
    const depositAmount = data.serviceProvider.depositAmount;
    const depositAmountType = data.serviceProvider.depositAmountType;
    const bookingPrice = data.price;

    let nonRefundableDeposit = 0;

    if (depositAmountType === "amount") {
      nonRefundableDeposit = depositAmount;
    } else if (depositAmount > 0) {
      nonRefundableDeposit = bookingPrice / depositAmount;
    } else {
      console.warn("Invalid deposit amount for calculation");
    }

    return convertToViewableAmount(nonRefundableDeposit);
  };

  const getAdminFee = (data) => {
    let amount = data * (5 / 100);
    return convertToViewableAmount(amount);
  };

  const getDepositPaymentSP = (depositAmount) => {
    let percentage = depositAmount * (5 / 100);

    return convertToViewableAmount(depositAmount - percentage);
  };

  useEffect(() => {
    getAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full h-full">
      <div className="w-[95%] mx-auto">
        <div className="w-full py-4 gap-x-2 flex items-center back-icon">
          <h1 className="font-semibold text-lg cursor-pointer">Appointments</h1>
          <DoubleLeftOutlined onClick={() => navigate(-1)} />
          <h1 className="font-semibold text-lg text-black-300">
            Appointment Details
          </h1>
        </div>

        <div className="w-full min-h-[calc(100vh-110px)] flex flex-col gap-y-4 box-shadow rounded-t-2xl p-5">
          {/* Two widgets */}
          <div className="w-full flex gap-x-4">
            {loading ? (
              <Skeleton
                style={{ height: "170px" }}
                className="flex-1 bg-red-100 p-4 rounded-lg"
                paragraph={{ rows: 4 }}
              />
            ) : (
              <div className="flex-1 bg-red-100 flex p-4 rounded-lg">
                <div className="w-8/12 space-y-3">
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Order ID:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      #{appointmentData?.uniqueId}
                    </p>
                  </div>
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Appointment Date:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      {commonDateFormat(appointmentData?.date)}
                    </p>
                  </div>
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Appointment Time:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      {dayjs(appointmentData?.startTime, "HH:mm:ss").format(
                        "hh:mmA"
                      )}
                      -
                      {dayjs(appointmentData?.endTime, "HH:mm:ss").format(
                        "hh:mmA"
                      )}
                    </p>
                  </div>
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">Service:</p>
                    <p className="leading-none text-sm text-black-600">
                      {appointmentData?.service?.serviceName}
                    </p>
                  </div>
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Promocode:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      {appointmentData?.isPromoCodeApplied
                        ? appointmentData?.promoCodeName
                        : "Not Applied"}
                    </p>
                  </div>
                </div>
                <div className="w-4/12 flex justify-center items-start pl-4">
                  {appointmentData?.status === "cancelled" ? (
                    <div
                      className={`"flex items-center justify-end border px-2 py-1.5 rounded-lg ${
                        appointmentData?.status === "finished"
                          ? "border-[green] bg-[#dfffde]"
                          : appointmentData?.status === "upcoming"
                          ? "border-yellow-500 bg-[#fffcde]"
                          : "border-red-900 bg-[#fff0f0]"
                      }`}
                    >
                      <span
                        className={`capitalize text-xs ${
                          appointmentData?.status === "finished"
                            ? "text-[green] border-[green]"
                            : appointmentData?.status === "upcoming"
                            ? "text-yellow-500 border-yellow-500"
                            : "text-red-900 border-red-900"
                        }`}
                      >
                        {appointmentData?.status === "upcoming"
                          ? "In progress"
                          : appointmentData?.status === "finished"
                          ? "Completed"
                          : appointmentData?.status}
                      </span>
                    </div>
                  ) : appointmentData?.status === "finished" ? (
                    <div
                      className={`"flex items-center justify-end border px-2 py-1.5 rounded-lg ${
                        appointmentData?.status === "finished"
                          ? "border-[green] bg-[#dfffde]"
                          : appointmentData?.status === "upcoming"
                          ? "border-yellow-500 bg-[#fffcde]"
                          : "border-red-900 bg-[#fff0f0]"
                      }`}
                    >
                      <span
                        className={`capitalize text-xs ${
                          appointmentData?.status === "finished"
                            ? "text-[green] border-[green]"
                            : appointmentData?.status === "upcoming"
                            ? "text-yellow-500 border-yellow-500"
                            : "text-red-900 border-red-900"
                        }`}
                      >
                        {appointmentData?.status === "upcoming"
                          ? "In progress"
                          : appointmentData?.status === "finished"
                          ? "Completed"
                          : appointmentData?.status}
                      </span>
                    </div>
                  ) : (
                    <div className="cursor-pointer">
                      <Dropdown
                        menu={{
                          items: updatedItems,
                          onClick: (e) =>
                            handleActionModalOpen(
                              "changeStatus",
                              e?.key,
                              appointmentData
                            ),
                        }}
                        trigger={["click"]}
                        disabled={appointmentData?.status === "cancelled"}
                      >
                        <div
                          className={`"flex items-center justify-end border px-2 py-1.5 rounded-lg ${
                            appointmentData?.status === "finished"
                              ? "border-[green] bg-[#dfffde]"
                              : appointmentData?.status === "upcoming"
                              ? "border-yellow-500 bg-[#fffcde]"
                              : "border-red-900 bg-[#fff0f0]"
                          }`}
                        >
                          <span
                            className={`capitalize text-xs ${
                              appointmentData?.status === "finished"
                                ? "text-[green] border-[green]"
                                : appointmentData?.status === "upcoming"
                                ? "text-yellow-500 border-yellow-500"
                                : "text-red-900 border-red-900"
                            }`}
                          >
                            {appointmentData?.status === "upcoming"
                              ? "Upcoming"
                              : appointmentData?.status === "finished"
                              ? "Completed"
                              : appointmentData?.status}
                          </span>
                          <button
                            className={`text-[10px] ml-1 ${
                              appointmentData?.status === "finished"
                                ? "text-[green] border-[green]"
                                : appointmentData?.status === "upcoming"
                                ? "text-yellow-500 border-yellow-500"
                                : "text-red-900 border-red-900"
                            }`}
                            onClick={(e) => e.preventDefault()}
                          >
                            <CaretDownFilled />
                          </button>
                        </div>
                      </Dropdown>
                    </div>
                  )}
                </div>
              </div>
            )}
            {loading ? (
              <Skeleton
                style={{ height: "170px" }}
                className="flex-1 bg-red-100 p-4 rounded-lg"
                paragraph={{ rows: 4 }}
              />
            ) : (
              <div className="flex-1 space-y-3 bg-red-100 p-4 rounded-lg">
                {/* <div className="w-full flex justify-between items-center">
                  <p className="leading-none font-medium text-sm">
                    {appointmentData?.service?.serviceName}:
                  </p>
                  <p className="leading-none text-sm text-black-600">
                    {convertToViewableAmount(appointmentData?.service?.price)}
                  </p>
                </div> */}
                <div className="w-full flex justify-between items-center">
                  <p className="leading-none font-medium text-sm">
                    Amount added to wallet:
                  </p>
                  <p className="leading-none text-sm text-black-600">
                    {/* {convertToViewableAmount(
                      appointmentData?.payType === "deposit"
                        ? appointmentData?.depositAmount -
                            (appointmentData?.depositAmount * 5) / 100 -
                            (appointmentData?.refunds
                              ? appointmentData?.refunds[0]?.amount
                              : 0)
                        : appointmentData?.serviceProvider?.depositAmount > 0
                        ? appointmentData?.serviceProvider?.depositAmount -
                          (appointmentData?.serviceProvider?.depositAmount *
                            5) /
                            100 -
                          (appointmentData?.refunds
                            ? appointmentData?.refunds[0]?.amount
                            : 0)
                        : appointmentData?.grandTotal -
                          (appointmentData?.grandTotal * 5) / 100 -
                          (appointmentData?.refunds
                            ? appointmentData?.refunds[0]?.amount
                            : 0)
                    )} */}

                    {!appointmentData?.isZeroPayment
                      ? appointmentData?.discount === 0 &&
                        appointmentData?.payType === "deposit"
                        ? getDepositPaymentSP(
                            appointmentData?.serviceProvider?.depositAmount
                          )
                        : getDepositPaymentSP(
                            appointmentData?.discount === 0
                              ? appointmentData?.status === "upcoming"
                                ? appointmentData?.grandTotal
                                : appointmentData?.isAutoRefund &&
                                  appointmentData.status === "cancelled"
                                ? 0
                                : appointmentData?.status === "finished"
                                ? appointmentData?.grandTotal
                                : appointmentData?.serviceProvider
                                    ?.depositAmount
                              : appointmentData?.discount > 0
                              ? appointmentData?.status === "upcoming"
                                ? appointmentData?.grandTotal
                                : appointmentData?.status === "finished"
                                ? appointmentData?.grandTotal
                                : appointmentData?.serviceProvider
                                    ?.depositAmount
                              : appointmentData?.grandTotal
                          )
                      : convertToViewableAmount(0)}
                  </p>
                </div>

                {appointmentData?.payType === "deposit" &&
                  !appointmentData?.isAutoRefund &&
                  !appointmentData?.isZeroPayment && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Deposit Payment:
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(
                          appointmentData?.depositAmount
                        )}
                      </p>
                    </div>
                  )}

                {appointmentData?.payType === "deposit" &&
                  !appointmentData?.isAutoRefund &&
                  appointmentData?.isZeroPayment && appointmentData?.status !== "cancelled" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Deposit Payment:
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(
                          appointmentData?.depositAmount
                        )}
                      </p>
                    </div>
                  )}

                {!appointmentData?.refunds &&
                  !appointmentData?.isZeroPayment &&
                  appointmentData?.payType === "payInAdvance" &&
                  !appointmentData?.isAutoRefund && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Pay in Advance:
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(appointmentData?.grandTotal)}
                      </p>
                    </div>
                  )}

                {appointmentData?.refunds &&
                  appointmentData?.payType === "payInAdvance" &&
                  appointmentData?.status !== "upcoming" &&
                  !appointmentData?.isAutoRefund && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Non Refundable Deposit:
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {nonRefundableAmount(appointmentData)}
                      </p>
                    </div>
                  )}

                {appointmentData?.isZeroPayment &&
                  appointmentData?.status === "cancelled" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Non Refundable Deposit:
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(0)}
                      </p>
                    </div>
                  )}

                {!appointmentData?.isAutoRefund && (
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Admin Fees:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      {!appointmentData?.isZeroPayment
                        ? "-" +
                          getAdminFee(
                            !appointmentData?.refunds &&
                              appointmentData?.payType === "payInAdvance" &&
                              !appointmentData?.isAutoRefund
                              ? appointmentData?.grandTotal
                              : appointmentData?.serviceProvider?.depositAmount
                          )
                        : convertToViewableAmount(0)}
                    </p>
                  </div>
                )}

                {appointmentData?.refunds &&
                  appointmentData?.isAutoRefund &&
                  appointmentData?.refunds[0]?.status === "approved" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        {appointmentData?.refunds[0]?.depositPayment > 0
                          ? "Deposit Payment :"
                          : "Deposit Refunded :"}
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {appointmentData?.refunds[0]?.depositPayment > 0
                          ? convertToViewableAmount(
                              appointmentData?.refunds[0]?.depositPayment
                            )
                          : "-" +
                            convertToViewableAmount(
                              appointmentData?.refunds[0]?.amount
                            )}
                      </p>
                    </div>
                  )}

                {appointmentData?.refunds &&
                  appointmentData?.isAutoRefund &&
                  appointmentData?.paymentStatus !== "paid" &&
                  appointmentData?.refunds[0]?.status === "approved" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Deposit Refunded :
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {"-" +
                          convertToViewableAmount(
                            appointmentData?.refunds[0]?.depositPayment
                          )}
                      </p>
                    </div>
                  )}

                {appointmentData?.refunds &&
                  appointmentData?.refunds[0]?.status === "approved" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        {appointmentData?.refunds[0]?.depositPayment > 0 &&
                          "Amount Refunded :"}
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {appointmentData?.refunds[0]?.depositPayment > 0 &&
                          "-" +
                            convertToViewableAmount(
                              appointmentData?.refunds[0]?.amount
                            )}
                      </p>
                    </div>
                  )}

                {appointmentData?.refunds &&
                  appointmentData?.refunds[0]?.status === "approved" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Remaining Payment :
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(
                          appointmentData?.remainingAmount
                        )}
                      </p>
                    </div>
                  )}

                {!appointmentData?.refunds &&
                  !appointmentData?.isZeroPayment && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Remaining Payment :
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(
                          appointmentData?.remainingAmount
                        )}
                      </p>
                    </div>
                  )}

                {appointmentData?.isZeroPayment &&
                  appointmentData?.status === "cancelled" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Remaining Payment Refunded :
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        -{convertToViewableAmount(0)}
                      </p>
                    </div>
                  )}

                {appointmentData?.isZeroPayment &&
                  appointmentData?.status !== "cancelled" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        Remaining Payment :
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {convertToViewableAmount(0)}
                      </p>
                    </div>
                  )}

                {appointmentData?.refunds &&
                  appointmentData?.refunds[0]?.status !== "approved" && (
                    <div className="w-full flex justify-between items-center">
                      <p className="leading-none font-medium text-sm">
                        {appointmentData?.isAutoRefund
                          ? "Remaining Payment :"
                          : "Remaining Payment Refunded :"}
                      </p>
                      <p className="leading-none text-sm text-black-600">
                        {appointmentData?.isAutoRefund
                          ? convertToViewableAmount(
                              appointmentData?.remainingAmount
                            )
                          : "-" +
                            convertToViewableAmount(
                              appointmentData?.serviceProvider?.depositAmount >
                                0
                                ? appointmentData?.service?.price -
                                    appointmentData?.discount -
                                    appointmentData?.serviceProvider
                                      ?.depositAmount
                                : appointmentData?.refunds[0]?.amount
                            )}
                      </p>
                    </div>
                  )}

                <Divider
                  className="border-black-700"
                  style={{ borderWidth: "1px" }}
                />
                <div className="w-full flex justify-between items-center">
                  <p className="leading-none font-medium text-sm">
                    Total Payment:
                  </p>
                  <p className="leading-none text-sm">
                    {convertToViewableAmount(
                      appointmentData?.service?.price -
                        appointmentData?.discount
                    )}
                  </p>
                </div>
                {appointmentData?.discount > 0 && (
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Discount:
                    </p>
                    <p className="leading-none text-sm text-black-600">
                      - {convertToViewableAmount(appointmentData?.discount)}
                    </p>
                  </div>
                )}
                {appointmentData?.discount > 0 && (
                  <div className="w-full flex justify-between items-center">
                    <p className="leading-none font-medium text-sm">
                      Sub Total:
                    </p>
                    <p className="leading-none text-sm">
                      {convertToViewableAmount(appointmentData?.service?.price)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="h-full w-full flex gap-x-4">
            {/* User Details */}
            <div className="w-4/6 flex flex-col gap-4">
              <div className="w-full flex border border-primary-500 rounded-lg">
                <div className="flex-1 border-r border-primary-500 p-3">
                  {/* Service Provider Widget Start */}
                  <h1 className="mb-2 font-semibold leading-none">
                    Service Provider
                  </h1>
                  {loading ? (
                    <Skeleton className="h-12" avatar paragraph={{ rows: 0 }} />
                  ) : (
                    <div className="flex items-start gap-x-3">
                      <img
                        src={
                          imgPath +
                          appointmentData?.service?.businessProfile?.image
                        }
                        alt={appointmentData?.serviceProvider?.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                      <div className="flex flex-col gap-y-1">
                        <p className="text-sm font-medium leading-none text-[#24242E]">
                          {appointmentData?.serviceProvider?.name}
                        </p>
                        <span className="text-xs text-[#12B500]">
                          {appointmentData?.service?.businessProfile?.user_name}
                        </span>
                      </div>
                    </div>
                  )}

                  {loading ? (
                    <Skeleton paragraph={{ rows: 1 }} />
                  ) : (
                    <div className="text-lightGray text-xs pt-2 flex flex-col gap-1">
                      <div className="flex items-center gap-x-2">
                        <img
                          src={LocationIcon}
                          alt="location-icon"
                          className="w-3 h-3"
                        />
                        <span>
                          {
                            appointmentData?.service?.businessProfile
                              ?.business_location
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <img
                          src={PhoneIcon}
                          alt="phone-icon"
                          className="w-3 h-3"
                        />
                        <span>
                          +44 {appointmentData?.serviceProvider?.phone_number}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <img
                          src={MailIcon}
                          alt="email-icon"
                          className="w-3 h-3"
                        />
                        <span>{appointmentData?.serviceProvider?.email}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Widget Start */}
                <div className="flex-1 p-3">
                  <h1 className="mb-2 leading-none font-semibold">Client</h1>
                  {loading ? (
                    <Skeleton className="h-12" avatar paragraph={{ rows: 0 }} />
                  ) : (
                    <div className="flex items-start gap-x-3">
                      {appointmentData?.client?.image ? (
                        <img
                          src={imgPath + appointmentData?.client?.image}
                          alt={appointmentData?.client?.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#FDE3E4] font-semibold text-primary-600">
                          {appointmentData?.client?.name
                            .split(" ")
                            .map((word) => word[0].toUpperCase())
                            .join("")
                            .slice(0, 2)}
                        </div>
                      )}

                      <p className="text-sm font-medium  text-[#24242E]">
                        {appointmentData?.client?.name}
                      </p>
                    </div>
                  )}

                  {loading ? (
                    <Skeleton paragraph={{ rows: 1 }} />
                  ) : (
                    <div className="text-lightGray text-xs pt-2 flex flex-col gap-1">
                      <div className="flex items-center gap-x-2">
                        <img
                          src={PhoneIcon}
                          alt="phone-icon"
                          className="w-3 h-3"
                        />
                        <span>+44 {appointmentData?.client?.phone_number}</span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <img
                          src={MailIcon}
                          alt="email-icon"
                          className="w-3 h-3"
                        />
                        <span>{appointmentData?.client?.email}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ratings and Reviews */}
              <div className="border border-primary-500 rounded-lg pt-3">
                <h1 className="leading-none font-semibold mb-3 px-3">
                  Rating & Review
                </h1>
                <RatingAndReviews />
              </div>
            </div>
            {/* Logs Start */}
            <div className="w-2/6 h-full flex flex-col">
              <div className="w-full border border-primary-500 rounded-lg p-3">
                <h1 className="leading-none font-semibold">Logs</h1>
                <BookingLogs />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        centered
        title={
          modalData.action === "delete"
            ? "Confirm Delete"
            : modalData?.action === "changeStatus"
            ? "Confirm Status Change"
            : "Confirm Selection Delete"
        }
        open={modalData.visible}
        onCancel={handleModalCancel}
        footer={
          <div>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <Button
              type="primary"
              className="text-white"
              onClick={
                modalData.action === "delete" ? undefined : handleChangeStatusOk
              }
            >
              Ok
              {btnLoading && (
                <span className="loader ml-2 align-middle mb-1"></span>
              )}
            </Button>
          </div>
        }
      >
        {modalData.action === "changeStatus"
          ? `Are you sure you want to change the status of this appointment to ${
              modalData.selectedStatus === "inProgress"
                ? "in progress"
                : modalData.selectedStatus
            }?`
          : `Are you sure you want to delete ${modalData?.name}?`}
      </Modal>
    </div>
  );
};

export default withDashboardLayout(AppointmentsDetails);

const RatingAndReviews = () => {
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const { id } = useParams();
  useEffect(() => {
    const getReviews = async () => {
      try {
        setLoading(true);
        const response = await GetAppointmentReviews(id);
        if (response?.isSuccess) {
          setReviews(response?.items);
        } else {
          message.error("Failed to get reviews, please try again later.");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        message.error(error?.message);
      }
    };
    getReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <section className="h-[40vh] overflow-y-scroll no-scrollbar">
      {!loading ? (
        <div className="no-scrollbar">
          {reviews && reviews?.length > 0 ? (
            reviews?.map((review, index) => {
              const isEven = index % 2 === 0;
              const backgroundColorClass = isEven
                ? "bg-white"
                : "bg-primary-300";

              return (
                <div
                  key={review?.id}
                  className={`w-full flex flex-col border-top py-4 px-6 gap-y-2 ${backgroundColorClass}`}
                >
                  <div className="flex justify-between">
                    <div className="flex gap-x-3">
                      <img
                        className="w-10 h-10 rounded-full"
                        src={
                          review?.business_Profile?.image
                            ? `${imgPath}${review?.business_Profile?.image}`
                            : Logo
                        }
                        alt="img"
                      />
                      <div className="flex flex-col w-full">
                        <span className="text-black-700 text-xs">
                          {review?.user?.name}
                        </span>
                        <span className="text-black-700 font-semibold text-[10px]">
                          @{review?.business_Profile?.user_name}
                        </span>
                        <div className="flex items-center gap-x-1">
                          <img src={StarIcon} width={16} alt="star-icon" />
                          <span className="text-lightGray text-sm">
                            {review?.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-lightGray">
                        {dayjs(review?.created_At).format(
                          "DD MMM YYYY, h:mm a"
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="w-full flex justify-between items-end">
                    <div className="w-10/12">
                      <p className="text-xs text-lightGray font-normal">
                        {review?.comment}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="w-full text-center my-8 font-medium text-xl">
              <Empty />
            </div>
          )}
        </div>
      ) : (
        <Loader height="h-full" />
      )}
    </section>
  );
};

const BookingLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const { id } = useParams();
  useEffect(() => {
    const getLogs = async () => {
      try {
        setLoading(true);
        const response = await GetAppointmentLogs(id);
        if (response?.isSuccess) {
          setLogs(response?.items);
        } else {
          message.error("Failed to get logs, please try again later.");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        message.error(error?.message);
      }
    };
    getLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="w-full h-full">
      {loading ? (
        <Loader height="h-[70vh]" />
      ) : logs && logs.length > 0 ? (
        logs.map((log) => (
          <div key={log.id} className="flex flex-col my-2 border-bottom pb-2">
            <div className="flex gap-x-2 items-center">
              <span className="w-1.5 h-1.5 flex-shrink-0 bg-primary-700 rounded-full" />
              <span className="list text-[11px] font-medium">{log.text}</span>
            </div>
            <div className="flex items-center justify-end gap-x-1">
              <img className="w-3 h-3" src={DateIcon} alt="date-icon" />
              <span className="text-[10px] text-right text-black-700">
                {dayjs(log.createdAt).format("DD MMM, YYYY h:mm A")}
              </span>
            </div>
          </div>
        ))
      ) : (
        <div className="flex items-center justify-center">
          <Empty description={<span>No messages</span>} />
        </div>
      )}
    </div>
  );
};
